// Hook to fetch the tenant

/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import {
    Tenant,
    TenantFilters,
    Tenant as TenantType
} from 'application/Shared/types/tenant';

/*  NPM  */
import axios from 'axios';
import { TFunction } from 'i18next';

/*  React  */
import { useCallback, useEffect, useState } from 'react';

interface GetTenantParams {
    includeClients?: boolean;
    id?: string;
    size?: number;
}

export const useFetchTenant = ({ clientId }: { clientId?: string }) => {
    const [tenant, setTenant] = useState<TenantType | boolean>(false); // false as this is the original behaviour, would prefer an empty object.
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetchData] = useState({});

    const refetchData = () => setShouldRefetchData({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const params: GetTenantParams = { size: 1 };
        if (clientId) {
            params.id = clientId;
            params.includeClients = true;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/tenant', {
                    cancelToken: source.token,
                    params
                });

                if (!Response.hasData(response)) {
                    debug.e('No Client found');
                } else {
                    setTenant(Response.getOne(response));
                }
            } catch (error) {
                debug.e('Unable to get Tenant', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [clientId, shouldRefetchData]);

    return {
        loading,
        refetchData,
        tenant
    };
};

export const usePurgeTenant = (t: TFunction<'translation', undefined>) => {
    const [saving, setSaving] = useState(false);

    const purgeTenant = useCallback(
        async (tenantId: string) => {
            try {
                setSaving(true);
                await axios.delete('/tenant/purge', {
                    params: { id: tenantId }
                });
                setSaving(false);
                return true; // success = true
            } catch (err) {
                debug.e(t('GlobalAdmin.Tenant.Edit.purgeError'), err);
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { purgeTenant, saving };
};

export const usePostRetroactivelyRedact = (
    t: TFunction<'translation', undefined>
) => {
    const [saving, setSaving] = useState(false);

    const redactRetroactively = useCallback(
        async (tenantId: string) => {
            try {
                setSaving(true);

                await axios.post('/tenant/redactRetroactively', {
                    id: tenantId
                });
                Notify.s(t('GlobalAdmin.Tenant.Edit.redactionSubmitted'));
                setSaving(false);
                return true; // success = true
            } catch (err) {
                debug.e(t('GlobalAdmin.Tenant.Edit.redactionError'), err);
                Notify.e(t('GlobalAdmin.Tenant.Edit.redactionError'));
                setSaving(false);
                return false; // success = false
            }
        },
        [t]
    );

    return { redactRetroactively, saving };
};

export const usePutTenant = () => {
    const [saving, setSaving] = useState(false);

    const putTenant = useCallback(
        async ({
            id,
            params
        }: {
            id: string;
            params: Partial<Tenant & TenantFilters>;
        }) => {
            setSaving(true);
            try {
                await axios.put(`/tenant/${id}`, params);
                return true; // success = true
            } catch (err) {
                debug.e('Unable to put tenant', err);
                return false; // success = false
            } finally {
                setSaving(false);
            }
        },
        []
    );

    return { putTenant, saving };
};
